import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Container, Typography, Paper, Button, TextField,
    CircularProgress, Chip, LinearProgress, Snackbar, Alert,
    IconButton, Tooltip
} from '@mui/material';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const JOURNAL_PROMPTS = [
    "What's one thing you learned from your most recent rejection?",
    "If you could give your past self advice about rejection, what would you say?",
    "What did rejection teach you that success never could?",
    "Name one door that closed and describe what opened instead.",
    "What strength did you discover in yourself after being told no?",
    "How has rejection shaped who you are today?",
    "What's the bravest thing you've done despite fear of rejection?",
    "Describe a moment when a 'no' turned out to be a gift.",
    "What would you attempt if you knew rejection was just feedback?",
    "Who in your life has been rejected and still succeeded? What did they do?",
];

const MOOD_CONFIG = [
    { value: 1, label: 'Rough', icon: SentimentVeryDissatisfiedIcon, color: '#ef4444' },
    { value: 2, label: 'Low', icon: SentimentDissatisfiedIcon, color: '#f97316' },
    { value: 3, label: 'Okay', icon: SentimentNeutralIcon, color: '#eab308' },
    { value: 4, label: 'Good', icon: SentimentSatisfiedIcon, color: '#22c55e' },
    { value: 5, label: 'Great', icon: SentimentVerySatisfiedIcon, color: '#5A7A62' },
];

const todayPrompt = () => {
    const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
    return JOURNAL_PROMPTS[dayOfYear % JOURNAL_PROMPTS.length];
};

const DailyUplift = () => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [mood, setMood] = useState(null);
    const [journal, setJournal] = useState('');
    const [streak, setStreak] = useState(0);
    const [alreadyDone, setAlreadyDone] = useState(false);
    const [savedToday, setSavedToday] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [notifEnabled, setNotifEnabled] = useState(false);
    const prompt = todayPrompt();

    const token = localStorage.getItem('token');

    const fetchToday = useCallback(async () => {
        if (!isAuthenticated || !token) { setLoading(false); return; }
        try {
            const { data } = await axios.get('/daily-checkin/today', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (data.checkin) {
                setMood(data.checkin.mood);
                setJournal(data.checkin.journal || '');
                setAlreadyDone(true);
            }
            setStreak(data.streak || 0);
        } catch (err) {
            // silent — first-time user
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, token]);

    useEffect(() => { fetchToday(); }, [fetchToday]);

    useEffect(() => {
        // Check if push notifications already granted
        if ('Notification' in window && Notification.permission === 'granted') {
            setNotifEnabled(true);
        }
    }, []);

    const handleSave = async () => {
        if (!mood) {
            setSnackbar({ open: true, message: 'Pick a mood first!', severity: 'warning' });
            return;
        }
        setSaving(true);
        try {
            const { data } = await axios.post('/daily-checkin', { mood, journal, prompt }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStreak(data.streak);
            setAlreadyDone(true);
            setSavedToday(true);
            setSnackbar({ open: true, message: `Check-in saved! ${data.streak} day streak 🔥`, severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Could not save. Try again.', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleEnableNotifications = async () => {
        if (!('Notification' in window)) {
            setSnackbar({ open: true, message: 'Notifications not supported in this browser.', severity: 'info' });
            return;
        }
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
            setNotifEnabled(true);
            // Schedule a daily reminder via a simple ServiceWorker message
            if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                    type: 'SCHEDULE_DAILY_REMINDER',
                    hour: 9,
                    title: 'Daily Uplift',
                    body: 'How are you feeling today? Your check-in is waiting.',
                });
            }
            setSnackbar({ open: true, message: 'Daily reminder enabled! We\'ll nudge you each morning.', severity: 'success' });
        } else {
            setSnackbar({ open: true, message: 'You can enable notifications in your browser settings anytime.', severity: 'info' });
        }
    };

    if (!isAuthenticated) {
        return (
            <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                <WhatshotIcon sx={{ fontSize: 64, color: '#4A3428', mb: 2 }} />
                <Typography
                  variant="h4"
                  fontWeight={700}
                  gutterBottom
                  sx={{ color: '#4A3428', fontFamily: '"Literata", Georgia, serif' }}
                >
                  Daily Uplift
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    A 60-second daily ritual. Log your mood, journal one thought,
                    and build a streak that reminds you: you show up every day.
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/auth')}
                    sx={{ borderRadius: '24px', textTransform: 'none', px: 4 }}
                >
                    Sign in to start your streak
                </Button>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{ color: '#4A3428', fontFamily: '"Literata", Georgia, serif' }}
                    >
                      Daily Uplift
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </Typography>
                </Box>
                <Tooltip title={notifEnabled ? 'Daily reminders on' : 'Enable daily reminder'}>
                    <IconButton onClick={handleEnableNotifications} color={notifEnabled ? 'primary' : 'default'}>
                        {notifEnabled ? <NotificationsActiveIcon /> : <NotificationsNoneIcon />}
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Streak card */}
            <Paper
                elevation={0}
                sx={{
                    p: 3, mb: 3, borderRadius: '16px', textAlign: 'center',
                    background: streak > 0
                        ? 'linear-gradient(135deg, rgba(74,52,40,0.08) 0%, rgba(139,111,92,0.12) 100%)'
                        : 'rgba(0,0,0,0.03)',
                    border: '1px solid',
                    borderColor: streak > 0 ? 'rgba(74,52,40,0.22)' : 'divider'
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={0.5}>
                    <WhatshotIcon sx={{ color: streak > 0 ? '#f97316' : '#9ca3af', fontSize: 32 }} />
                    <Typography variant="h3" fontWeight={800} sx={{ color: streak > 0 ? '#f97316' : '#9ca3af' }}>
                        {streak}
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {streak === 0
                        ? 'Start your streak today'
                        : streak === 1
                        ? '1 day streak. Keep going!'
                        : `${streak} day streak. You're on fire!`}
                </Typography>
                {streak > 0 && (
                    <LinearProgress
                        variant="determinate"
                        value={Math.min((streak / 7) * 100, 100)}
                        sx={{ mt: 1.5, borderRadius: '4px', height: 6, bgcolor: 'rgba(74,52,40,0.12)',
                            '& .MuiLinearProgress-bar': { bgcolor: '#4A3428' } }}
                    />
                )}
                {streak > 0 && streak < 7 && (
                    <Typography variant="caption" color="text.secondary">
                        {7 - streak} more day{7 - streak !== 1 ? 's' : ''} to your first 7-day milestone
                    </Typography>
                )}
                {streak >= 7 && (
                    <Chip label={`${Math.floor(streak / 7)} week${Math.floor(streak / 7) !== 1 ? 's' : ''} strong`}
                        size="small" sx={{ mt: 1, bgcolor: '#4A3428', color: 'white' }} />
                )}
            </Paper>

            {/* Mood picker */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                    How are you feeling today?
                </Typography>
                <Box display="flex" justifyContent="space-between" gap={1}>
                    {MOOD_CONFIG.map(({ value, label, icon: Icon, color }) => (
                        <Box
                            key={value}
                            onClick={() => !alreadyDone && setMood(value)}
                            sx={{
                                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                                gap: 0.5, p: 1.5, borderRadius: '12px', cursor: alreadyDone ? 'default' : 'pointer',
                                border: '2px solid',
                                borderColor: mood === value ? color : 'transparent',
                                bgcolor: mood === value ? `${color}15` : 'transparent',
                                transition: 'all 0.15s ease',
                                '&:hover': alreadyDone ? {} : { bgcolor: `${color}10`, borderColor: `${color}60` }
                            }}
                        >
                            <Icon sx={{ fontSize: 32, color: mood === value ? color : '#9ca3af' }} />
                            <Typography variant="caption" sx={{ color: mood === value ? color : 'text.secondary', fontWeight: mood === value ? 600 : 400 }}>
                                {label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Paper>

            {/* Journal */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
                    Today's reflection
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                    "{prompt}"
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Write as little or as much as you want. This is just for you."
                    value={journal}
                    onChange={(e) => !alreadyDone && setJournal(e.target.value)}
                    disabled={alreadyDone}
                    variant="outlined"
                    sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                        '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#374151' }
                    }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {journal.length} / 500 characters
                </Typography>
            </Paper>

            {/* Save / done state */}
            {alreadyDone ? (
                <Box textAlign="center" sx={{ py: 2 }}>
                    <Typography variant="h6" color="primary" fontWeight={600}>
                        {savedToday ? "Saved! See you tomorrow." : "You've already checked in today."}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Come back tomorrow to keep your streak alive.
                    </Typography>
                </Box>
            ) : (
                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleSave}
                    disabled={saving || !mood}
                    sx={{
                        borderRadius: '24px', textTransform: 'none', py: 1.5, fontSize: '1rem',
                        bgcolor: '#4A3428',
                        '&:hover': { bgcolor: '#3A281F' }
                    }}
                >
                    {saving ? <CircularProgress size={24} color="inherit" /> : 'Save today\'s check-in'}
                </Button>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    sx={{ borderRadius: '12px' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default DailyUplift;
